import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { Check, FilePlus2, Link2, Pencil, X } from 'lucide-react';
import Button from '../Button';
import { DIRECTION_LABELS, emailTypeLabel, carrierLabel } from '../../constants/shipments';
import { formatDate, formatDateTime, formatPercent, plainValue } from '../../utils/shipmentsFormat';
import { ReasonChips, StatusBadge, TrustBadge } from './StatusBadge';
import { Banner, ExternalLinkButton } from './Ui';

const Fact = ({ label, children }) => (children ? <div><dt>{label}</dt><dd className="shp-wrap" dir="auto">{children}</dd></div> : null);

// What the AI read from the e-mail (all plain text)
const Extraction = ({ x }) => {
  if (!x) return <p className="shp-muted shp-small" style={{ margin: 0 }}>AI z tohoto e-mailu nevyčetla žádné údaje.</p>;
  const numbers = Array.isArray(x.trackingNumbers) ? x.trackingNumbers.filter(Boolean) : [];
  return (
    <dl className="shp-facts">
      <Fact label="Dopravce">{x.carrier ? carrierLabel(x.carrier) : ''}</Fact>
      <Fact label={numbers.length > 1 ? 'Čísla zásilek' : 'Číslo zásilky'}>{numbers.join(', ')}</Fact>
      <Fact label="Číslo objednávky">{plainValue(x.orderRef)}</Fact>
      <Fact label="Popis zboží">{plainValue(x.description)}</Fact>
      <Fact label="Očekávané doručení">{formatDate(x.expectedDeliveryDate)}</Fact>
      <Fact label="Doručeno dne">{formatDate(x.deliveredDate)}</Fact>
      <Fact label="Výdejní místo">{plainValue(x.pickupPoint)}</Fact>
      <Fact label="Dobírka">{plainValue(x.cod)}</Fact>
    </dl>
  );
};

// One item of the review queue: the e-mail, why it needs a decision, what the AI extracted and the actions.
// `deletedId`: shipment id the API reported as deleted after an approve attempt (409 SHIPMENT_DELETED), 0 otherwise.
const ReviewCard = ({ item, busy, deletedId = 0, onApprove, onCorrect, onReject, onLink, onCreate }) => {
  const sender = item.fromName ? `${item.fromName} <${item.from}>` : item.from;
  const danger = item.reviewReasons?.includes('injection_suspected');
  const linked = item.shipmentId != null;
  const shipment = item.shipment;
  const shipmentId = Number(item.shipmentId);
  // a deleted shipment must be restored first: approving is blocked, rejecting still works
  const shipmentDeleted = deletedId > 0 || Boolean(shipment?.deletedAt);
  const deletedLinkId = Number.isInteger(deletedId) && deletedId > 0 ? deletedId : shipmentId;
  const hintId = useId();

  return (
    <article className={`shp-review__card ${danger ? 'shp-review__card--danger' : ''}`} aria-label={`E-mail: ${item.subject || 'bez předmětu'}`} aria-busy={busy}>
      <header style={{ display: 'grid', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <TrustBadge trust={item.trust} />
          {item.confidence != null && <span className="shp-tag">Jistota AI {formatPercent(item.confidence)}</span>}
          <span className="shp-tag shp-tag--info">{emailTypeLabel(item.emailType)}</span>
          {DIRECTION_LABELS[item.direction] && <span className="shp-tag">{DIRECTION_LABELS[item.direction]}</span>}
        </div>
        <h3 className="shp-wrap" dir="auto" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#333' }}>{item.subject || '(bez předmětu)'}</h3>
        <p className="shp-wrap shp-small shp-muted" dir="auto" style={{ margin: 0 }}>
          {sender} · <time dateTime={item.emailDate}>{formatDateTime(item.emailDate)}</time> · schránka {item.mailbox}
        </p>
      </header>

      {danger && (
        <Banner tone="bad"><strong>Pozor, podezření na podvržený obsah.</strong> Údaje neschvalujte, dokud je neověříte v původním e-mailu.</Banner>
      )}
      {shipmentDeleted && (
        <Banner tone="warn">
          <span id={hintId}>Zásilka je smazaná – nejdřív ji obnovte v záložce Smazané.</span>
          {Number.isInteger(deletedLinkId) && deletedLinkId > 0 && (
            <> <Link to={`/zasilky/${deletedLinkId}`} className="shp-link">Otevřít zásilku</Link></>
          )}
        </Banner>
      )}
      {item.reviewReasons?.length > 0 && <div className="shp-review__chips"><ReasonChips reasons={item.reviewReasons} /></div>}
      {item.snippet && <div className="shp-snippet shp-wrap" dir="auto">{item.snippet}</div>}
      <Extraction x={item.extraction} />

      {linked && shipment && (
        <p className="shp-small" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          Zásilka:
          <Link to={`/zasilky/${shipmentId}`} className="shp-wrap" dir="auto" style={{ fontWeight: 700 }}>{shipment.trackingNumber || shipment.orderRef || `č. ${shipmentId}`}</Link>
          <StatusBadge status={shipment.status} />
        </p>
      )}

      <div className="shp-review__actions">
        {linked ? (
          <>
            <Button icon={Check} loading={busy} disabled={shipmentDeleted} aria-describedby={shipmentDeleted ? hintId : undefined} onClick={onApprove}>Schválit</Button>
            <Button variant="secondary" icon={Pencil} disabled={busy || shipmentDeleted} aria-describedby={shipmentDeleted ? hintId : undefined} onClick={onCorrect}>Opravit a schválit</Button>
          </>
        ) : (
          <>
            <Button icon={Link2} disabled={busy} onClick={onLink}>Přiřadit k zásilce</Button>
            <Button variant="secondary" icon={FilePlus2} disabled={busy} onClick={onCreate}>Vytvořit zásilku</Button>
          </>
        )}
        <Button variant="danger" icon={X} disabled={busy} onClick={onReject}>Zamítnout</Button>
        <ExternalLinkButton kind="gmail" url={item.gmailUrl} className="shp-btn" unavailable="Odkaz na Gmail není dostupný">Otevřít v Gmailu</ExternalLinkButton>
      </div>
    </article>
  );
};

export default ReviewCard;
